// NetworkMonitor Component - Real-time network strength monitoring
// Based on documentation from docs/02_ROOM_CREATION_AND_PEER_CONNECTION.md

import React, { useState, useEffect } from 'react';
import '../styles/NetworkMonitor.css';

const NetworkMonitor = ({ className = '' }) => {
  const [networkStrength, setNetworkStrength] = useState('Checking...');
  const [bars, setBars] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [color, setColor] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const measureNetworkSpeed = async () => {
      try {
        const startTime = Date.now();
        const fileSize = 256 * 1024; // 256 KB
        
        const response = await fetch(`/netprobe?kb=256&t=${Date.now()}`, {
          method: 'GET',
          cache: 'no-store'
        });

        if (!response.ok) throw new Error('Network probe failed');

        await response.blob();
        const endTime = Date.now();
        const duration = (endTime - startTime) / 1000; // seconds
        const speedKbps = (fileSize * 8) / duration / 1024; // Kbps

        if (!isMounted) return;

        setSpeed(speedKbps);

        // Determine network strength
        let strength = 'Poor';
        let barCount = 1;
        let textcolor = 'text-danger';

        if (speedKbps > 5000) {
          strength = 'Excellent';
          barCount = 5;
          textcolor = 'text-primary';
        } else if (speedKbps > 2000) {
          strength = 'Good';
          barCount = 4;
          textcolor = 'text-success';
        } else if (speedKbps > 1000) {
          strength = 'Fair';
          barCount = 3;
          textcolor = 'text-warning';
        } else if (speedKbps > 500) {
          strength = 'Weak';
          barCount = 2;
          textcolor = 'text-secondary';
        } else {
          strength = 'Poor';
          barCount = 1;
          textcolor = 'text-danger';
        }

        setNetworkStrength(strength);
        setBars(barCount);
        setColor(textcolor);

      } catch (error) {
        console.error('Network speed test error:', error);
        if (isMounted) {
          setNetworkStrength('Unknown');
          setBars(0);
        }
      }
    };

    // Initial measurement
    measureNetworkSpeed();

    // Repeat every 30 seconds
    const interval = setInterval(measureNetworkSpeed, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  return (
    <div className={`mp-network mp-network-compact ${className}`.trim()}>
      <div className="mpn-bars" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((bar) => (
          <div
            key={bar}
            className={`mpn-bar ${bar <= bars ? 'active' : ''}`}
          ></div>
        ))}
      </div>
      <div className="mpn-labels">
        <span>Network</span>
        <span className={`mpn-text ${color}`}>{networkStrength}</span>
      </div>
      {speed > 0 && (
        <span className="mpn-speed">
          ({speed > 1000 
            ? `${(speed / 1000).toFixed(1)} Mbps` 
            : `${Math.round(speed)} Kbps`})
        </span>
      )}
    </div>
  );
};

export default NetworkMonitor;
